// src/modules/clinical-notes-v2/components/DraftNoteEditor.jsx
//
// Editor del draft de la nota clínica. TODOS los campos son libres —
// el doctor escribe directo. Cuando hay sugerencias IA (de care gaps
// o coding ops), aparecen abajo de la sección correspondiente en un
// AiSuggestionPanel (azul, opcional, con "Append to field above").

import React from "react";
import {
  Box,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
} from "@mui/material";

import EditIcon            from "@mui/icons-material/Edit";
import EventNoteIcon       from "@mui/icons-material/EventNote";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import MonitorHeartIcon    from "@mui/icons-material/MonitorHeart";
import MedicationIcon      from "@mui/icons-material/Medication";
import HistoryEduIcon      from "@mui/icons-material/HistoryEdu";
import PsychologyIcon      from "@mui/icons-material/Psychology";
import AssignmentIcon      from "@mui/icons-material/Assignment";

import AiSuggestionPanel from "./AiSuggestionPanel";

// =============================================================
// Helpers
// =============================================================

const getPath = (obj, path) =>
  path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);

const setPath = (obj, path, value) => {
  const keys = path.split(".");
  const out = { ...(obj || {}) };
  let cursor = out;
  for (let i = 0; i < keys.length - 1; i++) {
    cursor[keys[i]] = { ...(cursor[keys[i]] || {}) };
    cursor = cursor[keys[i]];
  }
  cursor[keys[keys.length - 1]] = value;
  return out;
};

const appendText = (prev, addition) => {
  if (!addition?.trim()) return prev || "";
  if (!prev?.trim()) return addition;
  return `${prev.trim()}\n\n${addition}`;
};

// =============================================================
// Section card wrapper — color por categoría
// =============================================================

const SECTION_PALETTE = {
  primary:   { headerBg: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)", icon: "#fff" },
  info:      { headerBg: "linear-gradient(135deg, #0288d1 0%, #0277bd 100%)", icon: "#fff" },
  success:   { headerBg: "linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)", icon: "#fff" },
  warning:   { headerBg: "linear-gradient(135deg, #ed6c02 0%, #e65100 100%)", icon: "#fff" },
  secondary: { headerBg: "linear-gradient(135deg, #6a1b9a 0%, #4a148c 100%)", icon: "#fff" },
  neutral:   { headerBg: "linear-gradient(135deg, #455a64 0%, #263238 100%)", icon: "#fff" },
};

function SectionCard({ tone = "primary", icon, title, edited, children }) {
  const palette = SECTION_PALETTE[tone] || SECTION_PALETTE.primary;
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        transition: "box-shadow .2s",
        "&:hover": { boxShadow: 1 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1, background: palette.headerBg, color: palette.icon }}
      >
        {icon}
        <Typography variant="subtitle1" fontWeight={800} sx={{ flex: 1, color: "inherit" }}>
          {title}
        </Typography>
        {edited && (
          <Chip
            size="small"
            icon={<EditIcon />}
            label="Edited"
            sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 700, height: 22 }}
          />
        )}
      </Stack>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
}

// =============================================================
// Field helper
// =============================================================

function DraftField({ draft, path, onPatch, editedPaths, label, multiline = false, rows = 1, placeholder, type }) {
  const value = getPath(draft, path) ?? "";
  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      placeholder={placeholder}
      multiline={multiline}
      minRows={multiline ? rows : undefined}
      maxRows={multiline ? Math.max(rows, 12) : undefined}
      type={type}
      value={value}
      onChange={(e) => onPatch(path, e.target.value)}
    />
  );
}

// =============================================================
// Main editor
// =============================================================

export default function DraftNoteEditor({
  draft,
  onChange,
  editedPaths,            // Set<string>
  // sugerencias acumuladas
  gaps = [],
  ops = [],
  careGapSuggestions = {},
  codingOpSuggestions = {},
}) {
  const patch = React.useCallback((path, value) => {
    onChange((prev) => setPath(prev || {}, path, value), path);
  }, [onChange]);

  const appendToPath = React.useCallback((path, addition) => {
    onChange((prev) => {
      const current = getPath(prev || {}, path) || "";
      return setPath(prev || {}, path, appendText(current, addition));
    }, path);
  }, [onChange]);

  const isEdited = (path) => editedPaths.has(path);
  const isEditedAny = (paths) => paths.some(isEdited);

  if (!draft) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={800}>Start writing the note</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The form is empty. Start typing in any section, or click <b>Generate AI Draft</b> in the
          header to let the assistant pre-fill it for you based on the selected care gaps and
          coding opportunities.
        </Typography>
      </Paper>
    );
  }

  const aiPanelProps = { gaps, ops, careGapSuggestions, codingOpSuggestions };

  return (
    <Stack spacing={2}>
      {/* Reason */}
      <SectionCard tone="primary" icon={<EventNoteIcon />} title="Reason for visit" edited={isEdited("reason.reasonForAppointment")}>
        <DraftField
          draft={draft}
          path="reason.reasonForAppointment"
          onPatch={patch}
          editedPaths={editedPaths}
          label="Reason for appointment"
        />
      </SectionCard>

      {/* HPI */}
      <SectionCard tone="info" icon={<HistoryEduIcon />} title="HPI / Chief complaint" edited={isEditedAny(["hpi.general", "hpi.tcm"])}>
        <Stack spacing={1.5}>
          <DraftField
            draft={draft} path="hpi.general" onPatch={patch} editedPaths={editedPaths}
            label="History of Present Illness" multiline rows={6}
          />
          <DraftField
            draft={draft} path="hpi.tcm" onPatch={patch} editedPaths={editedPaths}
            label="TCM / Additional summary (optional)" multiline rows={3}
          />
        </Stack>
        <AiSuggestionPanel
          section="hpi"
          sectionLabel="HPI"
          onAppendToField={(text) => appendToPath("hpi.general", text)}
          {...aiPanelProps}
        />
      </SectionCard>

      {/* Vitals */}
      <SectionCard tone="success" icon={<MonitorHeartIcon />} title="Vitals" edited={isEditedAny(
        ["weight","height","bmi","temperature","pulse","o2SatRa","bpPrevSys","bpPrevDia","bp1Sys","bp1Dia","bpOption1","bpLocation1"]
          .map((k) => `vitals.${k}`)
      )}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <DraftField draft={draft} path="vitals.weight"      onPatch={patch} editedPaths={editedPaths} label="Weight (lb)" />
            <DraftField draft={draft} path="vitals.height"      onPatch={patch} editedPaths={editedPaths} label="Height (in)" />
            <DraftField draft={draft} path="vitals.bmi"         onPatch={patch} editedPaths={editedPaths} label="BMI" />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <DraftField draft={draft} path="vitals.temperature" onPatch={patch} editedPaths={editedPaths} label="Temp (°F)" />
            <DraftField draft={draft} path="vitals.pulse"       onPatch={patch} editedPaths={editedPaths} label="Pulse" />
            <DraftField draft={draft} path="vitals.o2SatRa"     onPatch={patch} editedPaths={editedPaths} label="O2 Sat (%)" />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <DraftField draft={draft} path="vitals.bpPrevSys"   onPatch={patch} editedPaths={editedPaths} label="BP Prev Sys" />
            <DraftField draft={draft} path="vitals.bpPrevDia"   onPatch={patch} editedPaths={editedPaths} label="BP Prev Dia" />
            <DraftField draft={draft} path="vitals.bp1Sys"      onPatch={patch} editedPaths={editedPaths} label="BP #1 Sys" />
            <DraftField draft={draft} path="vitals.bp1Dia"      onPatch={patch} editedPaths={editedPaths} label="BP #1 Dia" />
          </Stack>
        </Stack>
      </SectionCard>

      {/* Medications */}
      <SectionCard tone="warning" icon={<MedicationIcon />} title="Medications" edited={isEditedAny(["meds.text","meds.allReviewedAndReconciled"])}>
        <Stack spacing={1.5}>
          <FormControlLabel
            control={
              <Switch
                checked={!!getPath(draft, "meds.allReviewedAndReconciled")}
                onChange={(e) => patch("meds.allReviewedAndReconciled", e.target.checked)}
              />
            }
            label="All medications reviewed and reconciled"
          />
          <DraftField draft={draft} path="meds.text" onPatch={patch} editedPaths={editedPaths} label="Current Medications" multiline rows={4} />
        </Stack>
      </SectionCard>

      {/* PMH */}
      <SectionCard tone="neutral" icon={<MedicalServicesIcon />} title="Past Medical History" edited={isEditedAny(["pmh.text","pmh.noChangeReviewed"])}>
        <Stack spacing={1.5}>
          <FormControlLabel
            control={
              <Switch
                checked={!!getPath(draft, "pmh.noChangeReviewed")}
                onChange={(e) => patch("pmh.noChangeReviewed", e.target.checked)}
              />
            }
            label="PMH reviewed — no change"
          />
          <DraftField draft={draft} path="pmh.text" onPatch={patch} editedPaths={editedPaths} label="Past Medical History" multiline rows={4} />
        </Stack>
      </SectionCard>

      {/* PHQ-2 */}
      <SectionCard tone="secondary" icon={<PsychologyIcon />} title="PHQ-2" edited={isEditedAny(["phq2.q1","phq2.q2","phq2.reviewedPhq9"])}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <DraftField draft={draft} path="phq2.q1"           onPatch={patch} editedPaths={editedPaths} label="Q1 (0-3)" />
          <DraftField draft={draft} path="phq2.q2"           onPatch={patch} editedPaths={editedPaths} label="Q2 (0-3)" />
          <DraftField draft={draft} path="phq2.reviewedPhq9" onPatch={patch} editedPaths={editedPaths} label="Reviewed" />
        </Stack>
      </SectionCard>

      {/* Assessment / Treatment / Follow up */}
      <SectionCard
        tone="primary"
        icon={<AssignmentIcon />}
        title="Assessment / Treatment / Follow-up"
        edited={isEditedAny(["assessPlanFu.assessments","assessPlanFu.treatment","assessPlanFu.followUp"])}
      >
        <Stack spacing={2}>
          <Box>
            <DraftField
              draft={draft} path="assessPlanFu.assessments" onPatch={patch} editedPaths={editedPaths}
              label="Assessments" multiline rows={5}
            />
            <AiSuggestionPanel
              section="assessment"
              sectionLabel="Assessment"
              onAppendToField={(text) => appendToPath("assessPlanFu.assessments", text)}
              {...aiPanelProps}
            />
          </Box>

          <Box>
            <DraftField
              draft={draft} path="assessPlanFu.treatment" onPatch={patch} editedPaths={editedPaths}
              label="Treatment / Plan of care" multiline rows={5}
            />
            <AiSuggestionPanel
              section="treatment"
              sectionLabel="Treatment / Plan"
              onAppendToField={(text) => appendToPath("assessPlanFu.treatment", text)}
              {...aiPanelProps}
            />
          </Box>

          <Box>
            <DraftField
              draft={draft} path="assessPlanFu.followUp" onPatch={patch} editedPaths={editedPaths}
              label="Follow up" multiline rows={3}
            />
            <AiSuggestionPanel
              section="followUp"
              sectionLabel="Follow-up"
              onAppendToField={(text) => appendToPath("assessPlanFu.followUp", text)}
              {...aiPanelProps}
            />
          </Box>
        </Stack>
      </SectionCard>
    </Stack>
  );
}
